import sha256 from 'crypto-js/sha256';
import { inject, observer } from 'mobx-react';
import { CardPaymentReturn, Payment } from 'payment-react-component';
import * as React from 'react';
import config from '../../../config';

import { IInvoicePaymentModalStore } from '../../../stores/InvoicePaymentModalStore';
import { ILanguageStore } from '../../../stores/LanguageStore';
import { IMandatesStore } from '../../../stores/MandatesStore';
import { ModalStore } from '../../../stores/ModalStore';
import { ITranslateStore } from '../../../stores/TranslateStore';

import { ISsoStore } from '../../../stores/SsoStore';
import RouteStore from '../../../stores/RouteStore';
import ClosableModal from './ClosableModal';

interface IPaymentModalProps {
  amount: number;
  headingLabel: string;
  referenceNumber: string;
  onRedirectToBank: (banklinkName: string) => void;
  amountFieldLabel?: string;
  languageStore?: ILanguageStore;
  invoicePaymentModalStore?: IInvoicePaymentModalStore;
  mandatesStore?: IMandatesStore;
  translateStore?: ITranslateStore;
  routeStore?: RouteStore;
  modalStore?: ModalStore;
  ssoStore?: ISsoStore;
}

const MANDATORY_SHOPPER_REFERENCE_PREFIX = 'telia-ee-';

@inject('invoicePaymentModalStore', 'languageStore', 'mandatesStore', 'ssoStore', 'routeStore', 'translateStore', 'modalStore')
@observer
export default class PaymentModal extends React.Component<IPaymentModalProps> {
  public render(): JSX.Element {
    return (
      <ClosableModal size="8-col" className="payment-modal" onClose={this.props.invoicePaymentModalStore.onModalClose}>
        {this.renderPaymentModal()}
      </ClosableModal>
    );
  }

  private renderPaymentModal(): JSX.Element {
    const language = this.props.languageStore.currentLanguage;
    const invoicePaymentModalStore = this.props.invoicePaymentModalStore;
    const cardTransactionReference = this.props.routeStore.queryParam('cardTransactionReference');
    const translate = this.props.translateStore.translate;

    const finalizePaymentProps = {
      onCompletedButton: this.onCompleted,
      onCloseButton: invoicePaymentModalStore.onModalClose,
      paymentSuccessInfo: translate('shared.payment-modal.successful-payment.details'),
      onRetryButton: this.onRetry
    };

    if (cardTransactionReference) {
      return (
        <CardPaymentReturn
          cardTransactionReference={cardTransactionReference}
          finalizePaymentProps={finalizePaymentProps}
          environment={config.paymentFrontendEnvironmentName}
          brand={'DIIL'}
          language={language}
        />
      );
    }
    return (
      <Payment
        source="SELF_SERVICE"
        brand="DIIL"
        initialAmount={this.props.amount}
        headingLabel={this.props.headingLabel}
        referenceNumber={this.props.referenceNumber}
        language={language}
        banklinkPayment={{
          successUrl: invoicePaymentModalStore.successUrl,
          failedUrl: invoicePaymentModalStore.failedUrl,
          processingUrl: invoicePaymentModalStore.processingUrl,
          onRedirectToBank: (banklinkName) => this.props.onRedirectToBank(banklinkName)
        }}
        cardPayment={{
          returnUrl: window.location.href,
          shopperReference: this.getShopperReference()
        }}
        noticeLabel={this.noticeLabel()}
        noticeType="attention"
        amountFieldLabel={this.props.amountFieldLabel}
        environment={config.paymentFrontendEnvironmentName}
        paymentMethods={['banklinks', 'card']}
        finalizePaymentProps={{
          onCloseButton: () => this.props.modalStore.closeModal(),
          onCompletedButton: () => window.location.reload(),
          paymentSuccessInfo: translate('shared.payment-modal.successful-payment.details')
        }}
      />
    );
  }

  private noticeLabel(): string {
    const mandate = this.props.mandatesStore.mandate;
    const translate = this.props.translateStore.translate;

    if (mandate.payload.billingMandateLevel === 'PARTIAL') {
      return translate('shared.payment-modal.notices.debt-notice-for-user-with-mandate');
    }

    if (mandate.payload.billingMandateLevel === 'FULL' && this.props.invoicePaymentModalStore.showDebtNotice) {
      return translate('shared.payment-modal.notices.debt-notice');
    }

    return null;
  }

  private getShopperReference(): string {
    const { person } = this.props.ssoStore;

    if (person) {
      return sha256(MANDATORY_SHOPPER_REFERENCE_PREFIX + person.personalCode.toString()).toString();
    }
    return null;
  }

  private onCompleted = () => {
    this.props.invoicePaymentModalStore.onModalClose();
    window.location.reload();
  };

  private onRetry = () => {
    this.props.invoicePaymentModalStore.onModalClose();
  };
}
